/**
 * Khung trang theo mẫu giao diện "Thông tin nguồn" — bốn vùng của khung dây mục 2.1:
 *   1. Thanh đầu trang  (data-vung="thanh-dau-trang")   topbar 70px
 *   2. Thanh điều hướng bên (data-vung="thanh-dieu-huong") menu dọc 300px nền navy
 *   3. Thanh ngữ cảnh   (data-vung="thanh-ngu-canh")    tỉnh, địa bàn, thời gian
 *   4. Vùng nội dung    (data-vung="vung-noi-dung")     nội dung + chân trang
 *
 * Các thuộc tính `data-vung` là HỢP ĐỒNG cho kiểm thử (giả định 7 của đợt 0):
 * KHÔNG đổi giá trị của chúng. Menu dọc có 10 mục `li` cấp một theo bản mẫu CMS
 * (thêm "Đăng xuất" khi đã đăng nhập); mục con của một nhóm là thẻ `a` trong khối
 * `div`, chỉ dựng khi nhóm đang mở.
 *
 * Lọc mục điều hướng theo quyền (chương 14): mục ngoài quyền không hiển thị, kể
 * cả mục con. Bộ chọn phạm vi địa bàn ở thanh ngữ cảnh.
 *
 * Chuông đầu trang (hợp đồng A0 hồ sơ – thông báo mục 5): huy hiệu = số thông báo
 * chưa đọc của máy chủ (thăm dò 30 giây, dừng khi tab ẩn) + việc nền chưa xem; mở ra
 * thấy 5 thông báo mới nhất, các việc nền như cũ và liên kết "Xem tất cả".
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { dienVao, vi } from '../ngon-ngu/vi';
import { viThongBao } from '../ngon-ngu/vi-thong-bao';
import { thongBaoApi, type ApiThongBao, type DongThongBao } from '../api/thong-bao-api';
import {
  moThongBao as moMotThongBao,
  nhanLoaiThongBao,
  thoiDiemThongBao,
} from '../man-hinh/thong-bao-tien-ich';
import { QUYEN_DIEU_HUONG, QUYEN_MAN_HINH } from '../quyen/ma-tran-man-hinh';
import { dungPhien } from '../trang-thai/ngu-canh-phien';
import { BoChonPhamViDiaBan, nhanPhamVi } from '../thanh-phan/BoChonPhamViDiaBan';
import { BieuTuong, type TenBieuTuong } from '../thanh-phan/BieuTuong';
import { NhanChuaCoApi } from '../thanh-phan/MauGiaoDien';
import { KhungThongBaoNhanh } from '../thanh-phan/ThongBaoNhanh';
import { dungCongViecNen, danhDauDaXemCongViecNen } from '../trang-thai/cong-viec-nen';
import { dungSoChuaDoc, dungThamDoSoChuaDoc } from '../trang-thai/thong-bao-chua-doc';
import './khung-trang.css';
import '../mau-sac/bang-mau.css';
import '../khung/giao-dien.css';

interface MucCon {
  duongDan: string;
  nhan: string;
  /** Quyền cần có để hiện mục con; không khai thì theo quyền của nhóm. */
  quyen?: string;
  /** Backend chưa có API: mục vẫn hiện, gắn nhãn "Chưa có API". */
  chuaCoApi?: boolean;
}

interface MucDieuHuong {
  /** Khoá ổn định của mục (dùng cho trạng thái mở/đóng nhóm). */
  khoa: string;
  nhan: string;
  bieuTuong: TenBieuTuong;
  /** Mục lá: đường dẫn. Nhóm: không có, dùng `con`. */
  duongDan?: string;
  /** Quyền cần có để hiện mục (mục lá) hoặc cả nhóm. */
  quyen?: string;
  /** Chỉ tài khoản cấp tỉnh thấy (ẩn với tài khoản xã dù có quyền trùng). */
  chiCapTinh?: boolean;
  chuaCoApi?: boolean;
  con?: MucCon[];
}

const mc = vi.menuCon;
const q = QUYEN_MAN_HINH;

/**
 * Cây menu theo bản mẫu CMS "Thông tin nguồn": 10 mục cấp một (thêm "Đăng xuất"
 * khi đã đăng nhập). Chức năng sẵn có mà bản mẫu không liệt kê được xếp vào nhóm
 * gần nhất (bản đồ, phát khẩn cấp/trực tiếp, tiếp sóng đã gỡ khỏi menu theo yêu cầu khách,
 * trang vẫn còn đường dẫn; người dùng, nhóm quyền,
 * danh mục hành chính, đa tỉnh, tích hợp vào Quản trị) để không mất chức năng.
 */
export const MUC_DIEU_HUONG: MucDieuHuong[] = [
  {
    khoa: 'tong-quan',
    nhan: vi.dieuHuong.tongQuan,
    bieuTuong: 'tong-quan',
    duongDan: '/tong-quan',
    quyen: QUYEN_DIEU_HUONG['/tong-quan'],
  },
  {
    khoa: 'cum-loa',
    nhan: vi.dieuHuong.thietBi,
    bieuTuong: 'loa',
    quyen: QUYEN_DIEU_HUONG['/thiet-bi'],
    con: [
      { duongDan: '/thiet-bi', nhan: mc.danhSachCumLoa },
      { duongDan: '/lich-phat', nhan: mc.lichPhatThanh, quyen: q['MH-17'] },
      { duongDan: '/thong-ke', nhan: mc.thongKeDaiPhatThanh, quyen: q['MH-33'] },
      { duongDan: '/nghe-lai', nhan: mc.nhatKyBanTinDaPhat, quyen: q['MH-16'] },
      { duongDan: '/lich-phat/nhat-ky-gui', nhan: mc.nhatKyLichPhatThietBi, quyen: q['MH-17'] },
      { duongDan: '/lich-phat/da-huy', nhan: mc.nhatKyHuyLichPhat, quyen: q['MH-17'] },
    ],
  },
  {
    khoa: 'bang-dien-tu',
    nhan: vi.dieuHuong.bangTinDienTu,
    bieuTuong: 'man-hinh',
    quyen: QUYEN_DIEU_HUONG['/bang-tin-dien-tu'],
    con: [
      { duongDan: '/bang-tin-dien-tu', nhan: mc.bangDienTu },
      { duongDan: '/bang-tin-dien-tu/lich-phat', nhan: mc.lichPhatBangTin },
      { duongDan: '/bang-tin-dien-tu/thong-ke', nhan: mc.thongKeTheoDiaBan, quyen: q['MH-33'] },
      { duongDan: '/bang-tin-dien-tu/nhat-ky-ban-tin', nhan: mc.nhatKyBanTinDaPhat },
      { duongDan: '/bang-tin-dien-tu/nhat-ky-lich', nhan: mc.nhatKyLichPhatThietBi },
      { duongDan: '/bang-tin-dien-tu/nhat-ky-huy', nhan: mc.nhatKyHuyLichPhat },
    ],
  },
  {
    khoa: 'bao-cao',
    nhan: vi.dieuHuong.baoCao,
    bieuTuong: 'bao-cao',
    quyen: QUYEN_DIEU_HUONG['/bao-cao'],
    con: [
      { duongDan: '/bao-cao/nhan-duyet', nhan: mc.nhanDuyetGuiBaoCao },
      { duongDan: '/bao-cao', nhan: mc.baoCaoDinhKy },
    ],
  },
  {
    khoa: 'bien-tap',
    nhan: vi.dieuHuong.banTin,
    bieuTuong: 'noi-dung',
    quyen: QUYEN_DIEU_HUONG['/ban-tin'],
    con: [
      { duongDan: '/ban-tin', nhan: mc.khoNoiDungCaNhan },
      { duongDan: '/ban-tin/duyet', nhan: mc.trinhPheDuyet, quyen: q['MH-14'] },
      { duongDan: '/ban-tin/xuat-ban', nhan: mc.xuatBan },
      { duongDan: '/ban-tin/kho-nguon', nhan: mc.khoThongTinNguon },
      { duongDan: '/kho-am-thanh', nhan: mc.khoAmThanh, quyen: q['MH-15'] },
    ],
  },
  {
    khoa: 'quan-tri',
    nhan: vi.dieuHuong.quanTri,
    bieuTuong: 'quan-tri',
    chiCapTinh: true,
    con: [
      { duongDan: '/quan-tri/cum-loa', nhan: mc.quanTriCumLoa, quyen: q['MH-04'] },
      { duongDan: '/quan-tri/bang-dien-tu', nhan: mc.quanTriBangDienTu, quyen: q['MH-25'] },
      { duongDan: '/quan-tri/bao-cao-dong', nhan: mc.cauHinhBaoCaoDong, quyen: q['MH-32'], chuaCoApi: true },
      { duongDan: '/danh-muc/linh-vuc', nhan: mc.linhVucCapHai, quyen: q['MH-37'] },
      { duongDan: '/quan-tri', nhan: mc.nguoiDung, quyen: q['MH-34'] },
      { duongDan: '/quan-tri/nhom-quyen', nhan: mc.nhomQuyen, quyen: q['MH-35'] },
      { duongDan: '/danh-muc', nhan: mc.donViHanhChinh, quyen: q['MH-36'] },
      { duongDan: '/quan-tri/da-tinh', nhan: mc.daTinh, quyen: q['MH-39'] },
      {
        duongDan: '/quan-tri/nha-cung-cap',
        nhan: mc.nhaCungCap,
        quyen: QUYEN_DIEU_HUONG['/quan-tri/nha-cung-cap'],
      },
      { duongDan: '/tich-hop', nhan: vi.dieuHuong.tichHop, quyen: QUYEN_DIEU_HUONG['/tich-hop'] },
    ],
  },
  {
    khoa: 'quan-ly-log',
    nhan: vi.dieuHuong.quanLyLog,
    bieuTuong: 'danh-muc',
    chiCapTinh: true,
    quyen: q['MH-38'],
    con: [
      { duongDan: '/quan-tri/nhat-ky/dieu-khien', nhan: mc.logDieuKhien },
      { duongDan: '/quan-tri/nhat-ky/dang-nhap', nhan: mc.logDangNhap },
      { duongDan: '/quan-tri/nhat-ky/trung-uong', nhan: mc.logTrungUong },
      { duongDan: '/quan-tri/nhat-ky/cau-hinh', nhan: mc.logCauHinh },
    ],
  },
  {
    khoa: 'thong-tin-ca-nhan',
    nhan: vi.dieuHuong.thongTinCaNhan,
    bieuTuong: 'nguoi-dung',
    duongDan: '/quan-tri/ho-so',
  },
  { khoa: 'huong-dan', nhan: vi.dieuHuong.huongDan, bieuTuong: 'huong-dan', duongDan: '/huong-dan' },
  {
    khoa: 'thong-bao',
    nhan: vi.dieuHuong.thongBao,
    bieuTuong: 'thong-bao',
    duongDan: '/thong-bao',
  },
];

/** Mục ở menu người dùng trên topbar: ai đăng nhập cũng xem được của chính mình. */
const MUC_NGUOI_DUNG: MucCon[] = [
  { duongDan: '/quan-tri/ho-so', nhan: vi.khung.thongTinCaNhan },
  { duongDan: '/quan-tri/phien', nhan: vi.khung.phienDangNhap },
];

/** Đường dẫn `duong` có thuộc (bằng hoặc nằm dưới) `goc` không. */
function thuoc(duong: string, goc: string): boolean {
  return duong === goc || duong.startsWith(`${goc}/`);
}

/** Tiêu đề trang trên topbar: nhãn của mục khớp đường dẫn dài nhất. */
function tieuDeTrang(duong: string, muc: MucDieuHuong[]): string {
  let tot: { dai: number; nhan: string } | null = null;
  const xet = (goc: string, nhan: string): void => {
    if (thuoc(duong, goc) && (!tot || goc.length > tot.dai)) tot = { dai: goc.length, nhan };
  };
  for (const m of muc) {
    if (m.duongDan) xet(m.duongDan, m.nhan);
    for (const c of m.con ?? []) xet(c.duongDan, c.nhan);
  }
  for (const c of MUC_NGUOI_DUNG) xet(c.duongDan, c.nhan);
  for (const [duongDan, nhan] of Object.entries(vi.khung.tieuDeTrangCon)) xet(duongDan, nhan);
  return (tot as { nhan: string } | null)?.nhan ?? vi.ungDung.tenNgan;
}

type CheDo = 'light' | 'dark';
const KHOA_CHE_DO = 'htttn-che-do';

/** Chế độ Sáng/Tối nhớ theo trình duyệt; bộ nhớ trình duyệt bị chặn thì mặc định Sáng. */
function docCheDo(): CheDo {
  try {
    return window.localStorage.getItem(KHOA_CHE_DO) === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

function ghiCheDo(cheDo: CheDo): void {
  try {
    window.localStorage.setItem(KHOA_CHE_DO, cheDo);
  } catch {
    /* bộ nhớ trình duyệt bị chặn: chỉ đổi trong phiên hiện tại */
  }
}

export interface KhungTrangProps {
  /** Nội dung thay cho <Outlet /> khi dùng ngoài bộ định tuyến (kiểm thử). */
  children?: ReactNode;
  /** Tên người dùng hiển thị ở thanh đầu trang. */
  hoTen?: string;
  /** Tên tỉnh hiển thị ở menu dọc, thanh đầu trang và thanh ngữ cảnh. */
  tenTinh?: string;
  onDangXuat?: () => void;
  /** API thông báo cho chuông — ghi đè được trong kiểm thử. */
  apiThongBao?: Pick<ApiThongBao, 'danhSach' | 'danhDauDaDoc'>;
}

/** Số thông báo mới nhất hiện trong hộp thả xuống của chuông. */
const SO_THONG_BAO_CHUONG = 5;

export function KhungTrang({
  children,
  hoTen,
  tenTinh,
  onDangXuat,
  apiThongBao = thongBaoApi,
}: KhungTrangProps): ReactNode {
  const phien = dungPhien();
  const { pathname } = useLocation();
  const dieuHuong = useNavigate();
  const [moChonPhamVi, datMoChonPhamVi] = useState(false);
  const [moThongBao, datMoThongBao] = useState(false);
  const [moMenuNguoiDung, datMoMenuNguoiDung] = useState(false);
  const [thuGon, datThuGon] = useState(false);
  const [cheDo, datCheDo] = useState<CheDo>(docCheDo);
  const [nhomMo, datNhomMo] = useState<string | null>(null);
  const congViecNen = dungCongViecNen();
  const soChuaXem = congViecNen.filter((c) => !c.daXem).length;
  // Thông báo máy chủ chỉ có khi đã đăng nhập (chế độ khung của đợt 0 không có hồ sơ).
  const daDangNhap = phien.hoSo !== null;
  dungThamDoSoChuaDoc(daDangNhap);
  const soChuaDoc = dungSoChuaDoc();
  const soHuyHieu = (daDangNhap ? soChuaDoc : 0) + soChuaXem;
  const [moiNhat, datMoiNhat] = useState<DongThongBao[] | null>(null);

  // Mở chuông thì tải 5 thông báo mới nhất; số chưa đọc đổi khi đang mở thì tải lại.
  useEffect(() => {
    if (!moThongBao || !daDangNhap) return undefined;
    const boHuy = new AbortController();
    datMoiNhat(null);
    apiThongBao
      .danhSach({ page: 1, pageSize: SO_THONG_BAO_CHUONG }, boHuy.signal)
      .then((kq) => {
        if (!boHuy.signal.aborted) datMoiNhat(kq?.items ?? []);
      })
      .catch(() => {
        if (!boHuy.signal.aborted) datMoiNhat([]);
      });
    return () => boHuy.abort();
  }, [moThongBao, daDangNhap, apiThongBao, soChuaDoc]);

  const bamThongBao = (d: DongThongBao): void => {
    datMoThongBao(false);
    void moMotThongBao(d, (id) => apiThongBao.danhDauDaDoc(id), dieuHuong);
  };
  useEffect(() => {
    document.documentElement.dataset.theme = cheDo;
    ghiCheDo(cheDo);
  }, [cheDo]);

  // Mục ngoài quyền KHÔNG hiển thị (chương 14). Khi chưa có hồ sơ (chế độ khung
  // của đợt 0) thì `coQuyen` luôn đúng nên hiện đủ 10 mục.
  // Nhóm chỉ hiện khi còn ít nhất một mục con được phép.
  const muc = useMemo(
    () =>
      MUC_DIEU_HUONG.filter((m) => !m.quyen || phien.coQuyen(m.quyen))
        .filter((m) => !(m.chiCapTinh && phien.khoaDiaBan))
        .map((m) => ({
          ...m,
          con: m.con?.filter((c) => !c.quyen || phien.coQuyen(c.quyen)),
        }))
        .filter((m) => !m.con || m.con.length > 0),
    [phien],
  );

  // Mục đang chọn = mục có đường dẫn khớp DÀI NHẤT với trang hiện tại, xét trên
  // toàn cây (vd. /quan-tri/nhat-ky/dang-nhap thuộc "Quản lý log", không phải
  // "Người dùng" /quan-tri). Nhóm chứa mục đó tự mở.
  const { duongDanDangChon, nhomHienTai } = useMemo(() => {
    let tot: { duong: string; nhom: string | null } | null = null;
    const xet = (duong: string, nhom: string | null): void => {
      if (thuoc(pathname, duong) && (!tot || duong.length > tot.duong.length)) tot = { duong, nhom };
    };
    for (const m of muc) {
      if (m.duongDan) xet(m.duongDan, null);
      for (const c of m.con ?? []) xet(c.duongDan, m.khoa);
    }
    const kq = tot as { duong: string; nhom: string | null } | null;
    return { duongDanDangChon: kq?.duong ?? null, nhomHienTai: kq?.nhom ?? null };
  }, [muc, pathname]);
  useEffect(() => {
    datNhomMo(nhomHienTai);
  }, [nhomHienTai]);

  const phamViDangDung = phien.phamViDangChon.length > 0 ? phien.phamViDangChon : phien.phamViDuocPhanCong;
  // Tài khoản cấp xã: tên xã hiện thường trực cạnh tên người dùng, phạm vi không đổi được.
  const xaKhoa = phien.khoaDiaBan;

  // Quản trị viên nền tảng đã CHUYỂN NGỮ CẢNH vào một tỉnh cụ thể (MH-39, đợt 3
  // phần C4): thanh đầu trang đổi màu và tên tỉnh hiển thị THƯỜNG TRỰC ở đó — để
  // tránh thao tác nhầm tỉnh khi đang ở ngữ cảnh hẹp thay vì xem toàn hệ thống.
  const dangONguCanhMotTinh = Boolean(phien.hoSo?.laQuanTriNenTang && phien.hoSo?.tenantId);
  const tinh = tenTinh ?? phien.hoSo?.tenTinh ?? undefined;
  const namHienTai = new Date().getFullYear();

  return (
    <div className={`khung-trang${thuGon ? ' khung-trang--thu-gon' : ''}`}>
      <header
        className={`thanh-dau-trang${dangONguCanhMotTinh ? ' thanh-dau-trang--ngu-canh-tinh' : ''}`}
        data-vung="thanh-dau-trang"
        data-ngu-canh-tinh={dangONguCanhMotTinh ? (tinh ?? '') : undefined}
      >
        <div className="thanh-dau-trang__trai">
          <button
            type="button"
            className="thanh-dau-trang__nut-menu"
            aria-label={thuGon ? vi.khung.moRongMenu : vi.khung.thuGonMenu}
            aria-expanded={!thuGon}
            onClick={() => datThuGon((x) => !x)}
          >
            <BieuTuong ten="menu" co={22} />
          </button>
          {/* Không dùng h1: mỗi màn hình đã có h1 riêng của nó. */}
          <p className="thanh-dau-trang__tieu-de">{tieuDeTrang(pathname, muc)}</p>
        </div>

        <div className="thanh-dau-trang__phai">
          <button
            type="button"
            role="switch"
            className="cong-tac-che-do"
            aria-checked={cheDo === 'dark'}
            aria-label={vi.khung.doiCheDo}
            onClick={() => datCheDo((x) => (x === 'dark' ? 'light' : 'dark'))}
          >
            <span>{cheDo === 'dark' ? vi.khung.cheDoToi : vi.khung.cheDoSang}</span>
            <i aria-hidden="true" />
          </button>

          <div data-vung="cong-viec-nen">
            <button
              type="button"
              className="thanh-dau-trang__chuong"
              aria-label={
                daDangNhap && soChuaDoc > 0
                  ? `${vi.thanhDauTrang.thongBao} — ${dienVao(viThongBao.chuong.nhanHuyHieu, { so: soChuaDoc })}`
                  : vi.thanhDauTrang.thongBao
              }
              aria-expanded={moThongBao}
              onClick={() => {
                datMoThongBao((x) => !x);
                danhDauDaXemCongViecNen();
              }}
            >
              <BieuTuong ten="chuong" co={22} />
              {soHuyHieu > 0 ? (
                <b
                  data-vung="huy-hieu-cong-viec-nen"
                  data-so-chua-doc={daDangNhap ? soChuaDoc : 0}
                  data-so-viec-nen={soChuaXem}
                  aria-hidden="true"
                >
                  {soHuyHieu > 99 ? '99+' : soHuyHieu}
                </b>
              ) : null}
            </button>
            {moThongBao ? (
              <div className="chuong__hop" data-vung="hop-chuong">
                {daDangNhap ? (
                  <section aria-label={viThongBao.chuong.tieuDeThongBao}>
                    <p className="chuong__tieu-de">{viThongBao.chuong.tieuDeThongBao}</p>
                    <ul role="list" data-vung="thong-bao-moi-nhat">
                      {moiNhat === null ? <li>{viThongBao.chuong.dangTai}</li> : null}
                      {moiNhat?.length === 0 ? <li>{viThongBao.chuong.chuaCoThongBao}</li> : null}
                      {moiNhat?.map((d) => (
                        <li key={d.id} className={d.daDoc ? undefined : 'chuong__muc--chua-doc'}>
                          <button
                            type="button"
                            className="chuong__muc"
                            data-da-doc={d.daDoc ? 'co' : 'khong'}
                            onClick={() => bamThongBao(d)}
                          >
                            <strong>{d.tieuDe}</strong>
                            <small>
                              {nhanLoaiThongBao(d.loai)} · {thoiDiemThongBao(d.createdAt)}
                            </small>
                          </button>
                        </li>
                      ))}
                    </ul>
                    <Link
                      to="/thong-bao"
                      className="chuong__xem-tat-ca"
                      data-vung="xem-tat-ca-thong-bao"
                      onClick={() => datMoThongBao(false)}
                    >
                      {viThongBao.chuong.xemTatCa}
                    </Link>
                  </section>
                ) : null}
                <section aria-label={viThongBao.chuong.tieuDeCongViec}>
                  {daDangNhap ? <p className="chuong__tieu-de">{viThongBao.chuong.tieuDeCongViec}</p> : null}
                  <ul data-vung="danh-sach-cong-viec-nen" role="list">
                    {congViecNen.length === 0 ? <li>{vi.thanhDauTrang.chuaCoCongViecNen}</li> : null}
                    {congViecNen.map((c) => (
                      <li key={c.id} data-trang-thai-cong-viec={c.trangThai}>
                        {c.moTa} —{' '}
                        {c.trangThai === 'thanh_cong'
                          ? vi.thanhDauTrang.congViecXongMoTa
                          : c.trangThai === 'that_bai'
                            ? `${vi.thanhDauTrang.congViecLoiMoTa}${c.maLoi ? `: ${c.maLoi}` : ''}`
                            : vi.thanhDauTrang.congViecDangXuLyMoTa}
                      </li>
                    ))}
                  </ul>
                </section>
              </div>
            ) : null}
          </div>

          <div className="nguoi-dung">
            <button
              type="button"
              className="nguoi-dung__nut"
              aria-label={vi.thanhDauTrang.hoSo}
              aria-expanded={moMenuNguoiDung}
              aria-haspopup="menu"
              onClick={() => datMoMenuNguoiDung((x) => !x)}
            >
              <span className="nguoi-dung__anh">
                <BieuTuong ten="nguoi-dung" co={22} />
              </span>
              <span className="nguoi-dung__chu">
                <strong>{hoTen ?? phien.hoSo?.hoTen ?? vi.thanhDauTrang.chuaDangNhap}</strong>
                {xaKhoa ? (
                  <span data-vung="ten-xa-dau-trang">{xaKhoa.ten}</span>
                ) : tinh ? (
                  <span>{tinh}</span>
                ) : null}
              </span>
            </button>
            {moMenuNguoiDung ? (
              <ul className="nguoi-dung__menu" role="menu" aria-label={vi.khung.menuNguoiDung}>
                {/* Đổi phạm vi địa bàn: trước nằm ở thanh ngữ cảnh (đã bỏ theo yêu cầu). */}
                <li role="none">
                  <button
                    type="button"
                    role="menuitem"
                    data-vung="nut-chon-pham-vi"
                    disabled={xaKhoa !== null}
                    onClick={() => {
                      datMoMenuNguoiDung(false);
                      datMoChonPhamVi(true);
                    }}
                  >
                    {vi.nguCanh.diaBan}:{' '}
                    {xaKhoa ? xaKhoa.ten : nhanPhamVi(phamViDangDung, phien.tenDiaBanTheoMa)}
                  </button>
                </li>
                {MUC_NGUOI_DUNG.map((c) => (
                  <li key={c.duongDan} role="none">
                    <NavLink role="menuitem" to={c.duongDan} onClick={() => datMoMenuNguoiDung(false)}>
                      {c.nhan}
                    </NavLink>
                  </li>
                ))}
                {onDangXuat ? (
                  <li role="none">
                    <button type="button" role="menuitem" onClick={onDangXuat}>
                      {vi.thanhDauTrang.dangXuat}
                    </button>
                  </li>
                ) : null}
              </ul>
            ) : null}
          </div>
        </div>
      </header>

      <nav className="thanh-dieu-huong" data-vung="thanh-dieu-huong" aria-label={vi.ungDung.tenNgan}>
        <div className="thanh-dieu-huong__dau">
          <span className="thanh-dieu-huong__logo" role="img" aria-label={vi.khung.logoTinh}>
            <BieuTuong ten="loa" co={48} />
          </span>
          <span className="thanh-dieu-huong__ten">{vi.ungDung.ten}</span>
          {tinh ? <span className="thanh-dieu-huong__tinh">{tinh}</span> : null}
        </div>
        <ul className="menu">
          {muc.map((m) => {
            const coCon = Boolean(m.con && m.con.length > 0);
            const mo = coCon && nhomMo === m.khoa;
            return (
              <li key={m.khoa}>
                {coCon ? (
                  <button
                    type="button"
                    className={`menu__muc${nhomHienTai === m.khoa ? ' menu__muc--trong-nhom' : ''}`}
                    aria-expanded={mo}
                    onClick={() => datNhomMo(mo ? null : m.khoa)}
                  >
                    <BieuTuong ten={m.bieuTuong} />
                    <span className="menu__nhan">{m.nhan}</span>
                    <BieuTuong ten="mui-ten-xuong" co={14} className="menu__mui-ten" />
                  </button>
                ) : (
                  <Link
                    to={m.duongDan ?? '/'}
                    className="menu__muc"
                    aria-current={m.duongDan === duongDanDangChon ? 'page' : undefined}
                  >
                    <BieuTuong ten={m.bieuTuong} />
                    <span className="menu__nhan">
                      {m.nhan}
                      {m.chuaCoApi ? <NhanChuaCoApi /> : null}
                    </span>
                    {m.khoa === 'thong-bao' && daDangNhap && soChuaDoc > 0 ? (
                      <b
                        className="menu__so"
                        data-vung="so-chua-doc-menu"
                        aria-label={dienVao(viThongBao.chuong.nhanHuyHieu, { so: soChuaDoc })}
                      >
                        {soChuaDoc > 99 ? '99+' : soChuaDoc}
                      </b>
                    ) : null}
                  </Link>
                )}
                {mo ? (
                  <div className="menu__con">
                    {m.con?.map((c) => (
                      <Link
                        key={c.duongDan}
                        to={c.duongDan}
                        className="menu__muc-con"
                        aria-current={c.duongDan === duongDanDangChon ? 'page' : undefined}
                      >
                        <span>{c.nhan}</span>
                        {c.chuaCoApi ? <NhanChuaCoApi /> : null}
                      </Link>
                    ))}
                  </div>
                ) : null}
              </li>
            );
          })}
          {onDangXuat ? (
            <li>
              <button type="button" className="menu__muc" onClick={onDangXuat}>
                <BieuTuong ten="dang-xuat" />
                <span className="menu__nhan">{vi.thanhDauTrang.dangXuat}</span>
              </button>
            </li>
          ) : null}
        </ul>
      </nav>

      <main className="vung-noi-dung" data-vung="vung-noi-dung">
        <div className="vung-noi-dung__than">{children ?? <Outlet />}</div>
        <footer className="chan-trang">
          <span>© {namHienTai}</span>
          <span>{vi.khung.phatTrienBoi}</span>
        </footer>
      </main>
      <KhungThongBaoNhanh />

      {moChonPhamVi && !xaKhoa ? (
        <BoChonPhamViDiaBan
          giaTri={phamViDangDung}
          onHuy={() => datMoChonPhamVi(false)}
          onXacNhan={(ds) => {
            phien.datPhamViDangChon(ds);
            datMoChonPhamVi(false);
          }}
        />
      ) : null}
    </div>
  );
}
